import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiEnvelope } from '../models/auth.model';
import { AddCartItem, Cart, CartBill, CartContext, CartItem, CartOption } from '../models/cart.model';

/** The server-backed cart — GET/PUT/DELETE /api/v1/cart and /cart/items(/{id}). */
@Injectable({ providedIn: 'root' })
export class CartApiService {
  private readonly base = environment.apiBase;

  constructor(private http: HttpClient) {}

  /** GET /cart — the resolved + priced cart (branchId is a fallback pricing hint). */
  get(branchId?: string | null): Observable<Cart> {
    let params = new HttpParams();
    if (branchId) params = params.set('branch_id', branchId);
    return this.http.get<ApiEnvelope<any>>(`${this.base}/api/v1/cart`, { params }).pipe(map((r) => mapCart(r.data)));
  }

  /**
   * PUT /cart — set order type / branch / delivery address / notes.
   * `dropPromo` answers the server's 409 `promo_not_eligible`: the change would
   * invalidate the applied offer, and `true` resends it accepting the removal.
   */
  setContext(ctx: CartContext, dropPromo = false): Observable<Cart> {
    const body: any = { drop_promo: dropPromo };
    if (ctx.orderType !== undefined) body.order_type = ctx.orderType;
    if (ctx.branchId !== undefined) body.branch_id = ctx.branchId;
    if (ctx.addressId !== undefined) body.address_id = ctx.addressId;
    if (ctx.kitchenNote !== undefined) body.kitchen_note = ctx.kitchenNote;
    if (ctx.deliveryNote !== undefined) body.delivery_note = ctx.deliveryNote;
    if (ctx.dineInTableToken !== undefined) body.dine_in_table_token = ctx.dineInTableToken;
    if (ctx.latitude != null) body.latitude = ctx.latitude;
    if (ctx.longitude != null) body.longitude = ctx.longitude;
    return this.http.put<ApiEnvelope<any>>(`${this.base}/api/v1/cart`, body).pipe(map((r) => mapCart(r.data)));
  }

  /** DELETE /cart — remove every line (keeps order-type/branch/address context). */
  clear(): Observable<Cart> {
    return this.http.delete<ApiEnvelope<any>>(`${this.base}/api/v1/cart`).pipe(map((r) => mapCart(r.data)));
  }

  /** POST /cart/items — append one configured line. */
  addItem(req: AddCartItem): Observable<Cart> {
    const body: any = {
      menu_item_id: req.menuItemId,
      menu_item_variant_id: req.variantId,
      quantity: req.quantity,
      options: (req.options || []).map((o) => ({
        kind: o.kind, id: o.id, group_id: o.groupId, ...(o.quantity != null ? { quantity: o.quantity } : {}),
      })),
    };
    if (req.branchId) body.branch_id = req.branchId;
    if (req.orderType) body.order_type = req.orderType;
    if (req.addressId) body.address_id = req.addressId;
    if (req.note) body.note = req.note;
    return this.http.post<ApiEnvelope<any>>(`${this.base}/api/v1/cart/items`, body).pipe(map((r) => mapCart(r.data)));
  }

  /** PATCH /cart/items/{id} — set a line's quantity (0 removes it). */
  setQuantity(lineId: string, quantity: number): Observable<Cart> {
    return this.http
      .patch<ApiEnvelope<any>>(`${this.base}/api/v1/cart/items/${encodeURIComponent(lineId)}`, { quantity })
      .pipe(map((r) => mapCart(r.data)));
  }

  /** DELETE /cart/items/{id} — remove one line. */
  removeLine(lineId: string): Observable<Cart> {
    return this.http
      .delete<ApiEnvelope<any>>(`${this.base}/api/v1/cart/items/${encodeURIComponent(lineId)}`)
      .pipe(map((r) => mapCart(r.data)));
  }

  /** POST /cart/promo — apply a promo/offer: typed `{ code }` or scanned `{ qr }`. */
  applyPromo(body: { code?: string; qr?: string }): Observable<Cart> {
    return this.http
      .post<ApiEnvelope<any>>(`${this.base}/api/v1/cart/promo`, body)
      .pipe(map((r) => mapCart(r.data)));
  }

  /** DELETE /cart/promo — remove the applied offer. */
  removePromo(): Observable<Cart> {
    return this.http
      .delete<ApiEnvelope<any>>(`${this.base}/api/v1/cart/promo`)
      .pipe(map((r) => mapCart(r.data)));
  }
}

function mapBill(b: any): CartBill {
  b = b || {};
  return {
    currencyCode: b.currency_code ?? 'EGP',
    vatInclusive: !!b.vat_inclusive,
    subtotal: b.subtotal ?? 0,
    serviceCharge: b.service_charge ?? 0,
    vatAmount: b.vat_amount ?? null,
    deliveryFee: b.delivery_fee ?? null,
    deliveryFeeOriginal: b.delivery_fee_original ?? null,
    freeDeliveryApplied: !!b.free_delivery_applied,
    discount: b.discount ?? b.discount_amount ?? null,
    total: b.total ?? 0,
  };
}

function mapItem(i: any): CartItem {
  return {
    id: i.id,
    menuItemId: i.menu_item_id,
    variantId: i.menu_item_variant_id,
    name: i.name,
    variantName: i.variant_name ?? null,
    imageUrl: i.image_url ?? null,
    quantity: i.quantity ?? 0,
    note: i.note ?? null,
    unitPrice: i.unit_price ?? 0,
    options: (i.options || []).map((o: any): CartOption => ({
      kind: o.kind, id: o.id, groupId: o.group_id, name: o.name, quantity: o.quantity ?? 1, unitPrice: o.unit_price ?? 0,
    })),
    lineTotal: i.line_total ?? 0,
    available: i.available !== false,
    unavailableReason: i.unavailable_reason ?? null,
  };
}

/** Map the API cart envelope `data` into our Cart model. */
export function mapCart(d: any): Cart {
  d = d || {};
  return {
    id: d.id ?? null,
    branchId: d.branch_id ?? null,
    orderType: d.order_type ?? null,
    addressId: d.address_id ?? null,
    kitchenNote: d.kitchen_note ?? null,
    deliveryNote: d.delivery_note ?? null,
    items: (d.items || []).map(mapItem),
    hasUnavailableItems: !!d.has_unavailable_items,
    bill: mapBill(d.bill),
    freeDelivery: {
      enabled: !!d.free_delivery?.enabled,
      threshold: d.free_delivery?.threshold ?? null,
      remaining: d.free_delivery?.remaining ?? null,
    },
    minOrder: { amount: d.min_order?.amount ?? null, met: d.min_order?.met !== false },
    promoCode: d.promo?.code ?? d.promo_code ?? null,
  };
}
